/**
 * RealmCatalog — realms read view (U2.5). 6 seeded realms (5 standard + 1
 * pocket). Reuses the shared `.race-*` catalog layout.
 */

import { useEffect, useMemo, useState } from "react";

import { fetchRealms, type RealmDTO } from "../api.js";

type LoadState =
  | { kind: "loading" }
  | { kind: "error"; message: string }
  | { kind: "ready"; realms: RealmDTO[] };

export function RealmCatalog() {
  const [state, setState] = useState<LoadState>({ kind: "loading" });
  const [selectedId, setSelectedId] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    fetchRealms()
      .then((realms) => {
        if (cancelled) return;
        setState({ kind: "ready", realms });
        if (realms.length > 0) setSelectedId(realms[0].id);
      })
      .catch((err: Error) => {
        if (!cancelled) setState({ kind: "error", message: err.message });
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const selected = useMemo(() => {
    if (state.kind !== "ready") return null;
    return state.realms.find((r) => r.id === selectedId) ?? null;
  }, [state, selectedId]);

  if (state.kind === "loading") return <div className="placeholder">Loading realms…</div>;
  if (state.kind === "error") {
    return (
      <div className="placeholder" style={{ color: "#ef4444" }}>
        <strong>Failed to load realms:</strong> {state.message}
      </div>
    );
  }

  return (
    <div className="race-catalog">
      <aside className="race-list">
        <div className="race-list-header">
          <h2>Realms</h2>
          <span className="count">{state.realms.length}</span>
        </div>
        <ul>
          {state.realms.map((r) => (
            <li key={r.id}>
              <button
                className={`race-list-item ${r.id === selectedId ? "active" : ""}`}
                onClick={() => setSelectedId(r.id)}
              >
                <span className="name">{r.name}</span>
                {r.type === "pocket" && <span className="badge immortal">pocket</span>}
              </button>
            </li>
          ))}
        </ul>
      </aside>
      <section className="race-detail">
        {selected ? <RealmDetail realm={selected} /> : <div className="placeholder">Pick a realm.</div>}
      </section>
    </div>
  );
}

function RealmDetail({ realm }: { realm: RealmDTO }) {
  return (
    <article>
      <header className="detail-header">
        <h2>{realm.name}</h2>
        <span className="category">{realm.type}</span>
      </header>

      {realm.description.length > 0 && (
        <section className="description">
          {realm.description.map((entry, i) => (
            <p key={i} className={`tiered tier-${entry.tier}`}>
              {entry.tier === "tier_2" && <span className="tier-badge">tier 2</span>}
              {entry.text}
            </p>
          ))}
        </section>
      )}

      {realm.type === "pocket" && (
        <p className="dossier-pending">A pocket dimension — created and bound to a character.</p>
      )}
    </article>
  );
}
